import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { Chart, elements, registerables, Tooltip } from 'chart.js';
import { BeatLoader } from 'react-spinners';
import '../../../../../styles/count_graphs.css';
import { barBackgroundColorLineCrossing, barBackgroundColorReverseLineCrossing } from '../../../../../config/index';
import { BiFullscreen, BiExitFullscreen, BiLineChart, BiBarChartAlt2, BiGitCompare } from 'react-icons/bi';
import { MdSwapVert } from 'react-icons/md';
import { transformMonthlyDataForComparison } from '../baseSuiteUtils';


const MonthlyCountChart = ({
    selectedYear,
    monthlyCountData,
    rawMonthlyData,
    monthlyDataLoading,
    selectedMonth,
    handleMonthlyBarClick,
    monthlyVisitors,
    monthlyVisitorPercentageIncrease,
    isTrafficView = false,
    setIsTrafficView,
    isCompareMode = false,
    setIsCompareMode,
    processedCameraData = []
}) => {
    const [barChartData, setBarChartData] = useState();
    const [lineChartData, setLineChartData] = useState();
    const [compareChartData, setCompareChartData] = useState(null);
    const [chartKey, setChartKey] = useState(0); // Key to force chart remount

    // Auto-enable traffic view when compare mode is turned on
    useEffect(() => {
        if (isCompareMode && !isTrafficView) {
            setIsTrafficView(true);
        }
    }, [isCompareMode]);

    const getMonthName = (monthNumber) => {
        const months = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];
        return months[monthNumber - 1];
    };

    const smallScreen = window.innerWidth < 786;

    const [chartType, setChartType] = useState('bar');
    const [isFullscreen, setIsFullscreen] = useState(false);

    const barChartRef = useRef(null);
    const lineChartRef = useRef(null);

    // Check if there are multiple cameras available for comparison
    const hasMultipleCameras = useMemo(() => {
        if (!processedCameraData || processedCameraData.length === 0) return false;
        const cameras = processedCameraData.filter(cam => cam.camera_id !== 'All' && cam.camera_id !== null);
        return cameras.length > 1;
    }, [processedCameraData]);

    // Calculate totals differently based on compare mode
    const { totalPeopleIn, totalPeopleOut } = useMemo(() => {
        if (isCompareMode && rawMonthlyData && processedCameraData) {
            // In compare mode: calculate totals from raw data for each camera
            const cameras = processedCameraData.filter(cam => cam.camera_id !== 'All' && cam.camera_id !== null);

            let totalIn = 0;
            let totalOut = 0;

            cameras.forEach(camera => {
                const cameraData = rawMonthlyData.filter(d => d.camera_id === camera.camera_id);

                const cameraInSum = cameraData
                    .filter(d => d.event_type === 'Line Crossing')
                    .reduce((sum, d) => sum + parseInt(d.total_count, 10), 0);

                const cameraOutSum = cameraData
                    .filter(d => d.event_type === 'Reverse Line Crossing')
                    .reduce((sum, d) => sum + parseInt(d.total_count, 10), 0);

                totalIn += cameraInSum;
                totalOut += cameraOutSum;
            });

            return { totalPeopleIn: totalIn, totalPeopleOut: totalOut };
        } else {
            // Normal mode: sum from aggregated monthly data
            const totalIn = monthlyCountData.reduce((sum, d) => sum + d.event_types["Line Crossing"], 0);
            const totalOut = monthlyCountData.reduce((sum, d) => sum + d.event_types["Reverse Line Crossing"], 0);
            return { totalPeopleIn: totalIn, totalPeopleOut: totalOut };
        }
    }, [isCompareMode, rawMonthlyData, processedCameraData, monthlyCountData]);

    const prepareMonthlyCountBarChart = () => {
        if (!monthlyCountData || monthlyCountData.length === 0) {
            return {
                labels: [],
                datasets: []
            };
        } else {
            if (isTrafficView) {
                // Traffic view: show average of in and out
                return {
                    labels: monthlyCountData.map(d => getMonthName(d.month)),
                    datasets: [
                        {
                            label: 'Traffic (Average)',
                            data: monthlyCountData.map(d =>
                                (d.event_types['Line Crossing'] + d.event_types['Reverse Line Crossing']) / 2
                            ),
                            backgroundColor: '#FFA500', // Orange color for traffic
                            borderColor: monthlyCountData.map(d => (selectedMonth === d.month ? '#ccc' : '#FF8C00')),
                            borderWidth: 1,
                        }
                    ],
                };
            } else {
                // Normal view: show in and out separately
                return {
                    labels: monthlyCountData.map(d => getMonthName(d.month)),
                    datasets: [
                        {
                            label: 'People In',
                            data: monthlyCountData.map(d => d.event_types['Line Crossing']),
                            backgroundColor: barBackgroundColorLineCrossing,
                            borderColor: monthlyCountData.map(d => (selectedMonth === d.month ? '#ccc' : '#28547F')),
                            borderWidth: 1,
                        },
                        {
                            label: 'People Out',
                            data: monthlyCountData.map(d => d.event_types['Reverse Line Crossing']),
                            backgroundColor: barBackgroundColorReverseLineCrossing,
                            borderColor: monthlyCountData.map(d => (selectedMonth === d.month ? '#ccc' : '#8E338C')),
                            borderWidth: 1,
                        }
                    ],
                };
            }
        }
    };

    const prepareMonthlyCountLineChart = () => {
        if (!monthlyCountData || monthlyCountData.length === 0) {
            return {
                labels: [],
                datasets: []
            };
        } else {
            if (isTrafficView) {
                // Traffic view: show average of in and out
                return {
                    labels: monthlyCountData.map(d => getMonthName(d.month)),
                    datasets: [
                        {
                            label: 'Traffic (Average)',
                            data: monthlyCountData.map(d =>
                                (d.event_types['Line Crossing'] + d.event_types['Reverse Line Crossing']) / 2
                            ),
                            backgroundColor: '#FFA500',
                            borderColor: '#FFA500',
                        }
                    ],
                };
            } else {
                // Normal view: show in and out separately
                return {
                    labels: monthlyCountData.map(d => getMonthName(d.month)),
                    datasets: [
                        {
                            label: 'People In',
                            data: monthlyCountData.map(d => d.event_types['Line Crossing']),
                            backgroundColor: barBackgroundColorLineCrossing,
                            borderColor: barBackgroundColorLineCrossing,
                        },
                        {
                            label: 'People Out',
                            data: monthlyCountData.map(d => d.event_types['Reverse Line Crossing']),
                            backgroundColor: barBackgroundColorReverseLineCrossing,
                            borderColor: barBackgroundColorReverseLineCrossing,
                        }
                    ],
                };
            }
        }
    }

    // For bar chart
    const getBarChartOptions = () => {
        return {
            responsive: true,
            maintainAspectRatio: !isFullscreen,
            onClick: (event, elements) => {
                if (elements.length > 0) {
                    const index = elements[0].index;
                    const monthNumber = monthlyCountData[index]?.month;
                    if (monthNumber !== undefined) {
                        handleMonthlyBarClick(monthNumber);
                    }
                }
            },
            hover: {
                mode: 'nearest',
                intersect: false,
            },
            onHover: function (event, elements) {
                const chartElement = this.canvas;
                if (elements && elements.length > 0) {
                    chartElement.style.cursor = 'pointer';
                } else {
                    chartElement.style.cursor = 'default';
                }
            },
            plugins: {
                legend: {
                    labels: {
                        color: '#F8F8F8'
                        },
                    onClick: (event, legendItem, legend) => {
                        // Enable clicking legend items to toggle camera visibility
                        const index = legendItem.datasetIndex;
                        const chart = legend.chart;
                        const meta = chart.getDatasetMeta(index);

                        // Toggle visibility
                        meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;
                        chart.update();
                    },
                    onHover: (event) => {
                        event.native.target.style.cursor = 'pointer';
                    },
                    onLeave: (event) => {
                        event.native.target.style.cursor = 'default';
                    },
                }
            },
            scales: {
                x: {
                    stacked: !isTrafficView && !isCompareMode, // Don't stack in traffic view or compare mode
                    grid: { display: false },
                    barPercentage: 0.5,
                    categoryPercentage: 0.5,
                    ticks: {
                        color: (context) => {
                            const labelMonth = getMonthName(monthlyCountData[context.index]?.month);
                            return labelMonth === getMonthName(selectedMonth) ? '#32de84' : '#fff';
                        },
                        font: {
                            weight: (context) => {
                                const labelMonth = getMonthName(monthlyCountData[context.index]?.month);
                                return labelMonth === getMonthName(selectedMonth) ? 'bold' : 'normal';
                            },
                            size: (context) => {
                                const labelMonth = getMonthName(monthlyCountData[context.index]?.month);
                                return labelMonth === getMonthName(selectedMonth) ? 14 : 12;
                            }
                        }
                    }
                },
                y: {
                    stacked: !isTrafficView && !isCompareMode, // Don't stack in traffic view or compare mode
                    beginAtZero: true,
                    ticks: { font: { size: 12 }, color: '#F8F8F8' },
                }
            }
        };
    };
    // For line chart
    const getLineChartOptions = () => {
        return {
            responsive: true,
            maintainAspectRatio: !isFullscreen,
            elements: {
                line: {
                    tension: 0.3,
                    borderWidth: 3
                },
                point: {
                    radius: 4,
                    hoverRadius: 6
                }
            },
            onClick: (event, elements) => {
                if (elements.length > 0) {
                    const index = elements[0].index;
                    const monthNumber = monthlyCountData[index]?.month;
                    if (monthNumber !== undefined) {
                        handleMonthlyBarClick(monthNumber);
                    }
                }
            },
            hover: {
                mode: 'nearest',
                intersect: false,
            },
            onHover: function (event, elements) {
                const chartElement = this.canvas;
                if (elements && elements.length > 0) {
                    chartElement.style.cursor = 'pointer';
                } else {
                    chartElement.style.cursor = 'default';
                }
            },
            plugins: {
                legend: {
                    labels: {
                        color: '#F8F8F8'
                        },
                    onClick: (event, legendItem, legend) => {
                        // Enable clicking legend items to toggle camera visibility
                        const index = legendItem.datasetIndex;
                        const chart = legend.chart;
                        const meta = chart.getDatasetMeta(index);

                        // Toggle visibility
                        meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;
                        chart.update();
                    },
                    onHover: (event) => {
                        event.native.target.style.cursor = 'pointer';
                    },
                    onLeave: (event) => {
                        event.native.target.style.cursor = 'default';
                    },
                }
            },
            scales: {
                x: {
                    grid: { display: false },
                    ticks: {
                        color: (context) => {
                            const labelMonth = getMonthName(monthlyCountData[context.index]?.month);
                            return labelMonth === getMonthName(selectedMonth) ? '#32de84' : '#fff';
                        },
                        font: {
                            weight: (context) => {
                                const labelMonth = getMonthName(monthlyCountData[context.index]?.month);
                                return labelMonth === getMonthName(selectedMonth) ? 'bold' : 'normal';
                            },
                            size: (context) => {
                                const labelMonth = getMonthName(monthlyCountData[context.index]?.month);
                                return labelMonth === getMonthName(selectedMonth) ? 14 : 12;
                            }
                        }
                    }
                },
                y: {
                    beginAtZero: true,
                    ticks: { font: { size: 12 }, color: '#F8F8F8' },
                }
            }
        };
    };


    const toggleChartType = () => {
        setChartType(prevType => prevType === 'bar' ? 'line' : 'bar');
    };


    // Force chart resize when entering/exiting fullscreen
    useEffect(() => {
        const timers = [];

        // Force chart remount when exiting fullscreen to reset sizing
        if (!isFullscreen) {
            setChartKey(prev => prev + 1);
        }

        // Force immediate DOM reflow
        const forceReflow = (chartRef) => {
            if (chartRef?.canvas) {
                const container = chartRef.canvas.parentElement;
                if (container) {
                    void container.offsetHeight;
                }
            }
        };

        forceReflow(barChartRef.current);
        forceReflow(lineChartRef.current);

        // First resize immediately
        if (barChartRef.current) {
            barChartRef.current.resize();
        }
        if (lineChartRef.current) {
            lineChartRef.current.resize();
        }

        // Second resize after a short delay
        timers.push(setTimeout(() => {
            if (barChartRef.current) {
                barChartRef.current.resize();
                barChartRef.current.update('none');
            }
            if (lineChartRef.current) {
                lineChartRef.current.resize();
                lineChartRef.current.update('none');
            }
        }, 50));

        // Third resize after CSS transition
        timers.push(setTimeout(() => {
            if (barChartRef.current) {
                barChartRef.current.resize();
                barChartRef.current.update('none');
            }
            if (lineChartRef.current) {
                lineChartRef.current.resize();
                lineChartRef.current.update('none');
            }
        }, 350));

        // Final resize to ensure everything is settled
        timers.push(setTimeout(() => {
            if (barChartRef.current) {
                barChartRef.current.resize();
                barChartRef.current.update('none');
            }
            if (lineChartRef.current) {
                lineChartRef.current.resize();
                lineChartRef.current.update('none');
            }
        }, 500));

        return () => timers.forEach(timer => clearTimeout(timer));
    }, [isFullscreen]);

    // Handle window resize events in fullscreen mode
    useEffect(() => {
        if (!isFullscreen) return;

        const handleResize = () => {
            if (barChartRef.current) {
                barChartRef.current.resize();
                barChartRef.current.update('none');
            }
            if (lineChartRef.current) {
                lineChartRef.current.resize();
                lineChartRef.current.update('none');
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isFullscreen]);


    // Prepare comparison data when in compare mode
    useEffect(() => {
        if (isCompareMode && rawMonthlyData && processedCameraData.length > 0) {
            const { cameraDatasets, allMonths } = transformMonthlyDataForComparison(
                rawMonthlyData,
                processedCameraData,
                isTrafficView
            );

            setCompareChartData({
                labels: allMonths.map(m => getMonthName(m)),
                datasets: cameraDatasets
            });
        }
    }, [isCompareMode, rawMonthlyData, processedCameraData, isTrafficView]);

    useEffect(() => {
        if (monthlyCountData !== null && monthlyCountData !== undefined) {
            const preparedBarChart = prepareMonthlyCountBarChart();
            if (preparedBarChart) {
                setBarChartData(preparedBarChart);
            }

            const preparedLineChart = prepareMonthlyCountLineChart();
            if (preparedLineChart) {

                setLineChartData(preparedLineChart);
            }

        }

    }, [monthlyCountData, selectedYear, chartType, isTrafficView, isFullscreen]);

    Chart.register(...registerables);

    return (
        <div className={`${monthlyDataLoading ? 'daily-bar-chart-container-loading' : ''} ${isFullscreen ? 'count-bar-chart-wrapper fullscreen-container' : 'count-bar-chart-wrapper'}`}>
            {
                monthlyCountData.length > 0 ?
                    <>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <div className='graph-title' style={{ flex: '0 1 auto', minWidth: '0', maxWidth: 'fit-content' }}>{selectedYear} Monthly </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'nowrap', flexShrink: 1 }}>
                                <div style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
                                <button
                                    className='toggle-chart-button'
                                    onClick={() => setIsCompareMode(!isCompareMode)}
                                    title={
                                        !hasMultipleCameras
                                            ? 'Multiple cameras required for comparison'
                                            : (isCompareMode ? 'Switch to Single Camera View' : 'Compare All Cameras')
                                    }
                                    disabled={!hasMultipleCameras}
                                    style={{
                                        backgroundColor: isCompareMode ? '#32de84' : undefined,
                                        opacity: !hasMultipleCameras ? 0.5 : 1,
                                        cursor: !hasMultipleCameras ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    <BiGitCompare size={24} color="white" />
                                </button>
                                <button
                                    className='toggle-chart-button'
                                    onClick={() => setIsTrafficView(!isTrafficView)}
                                    title={
                                        isCompareMode
                                            ? 'Traffic view is required in compare mode'
                                            : (isTrafficView ? 'Switch to In/Out Count View' : 'Switch to Traffic (Average) View')
                                    }
                                    disabled={isCompareMode}
                                    style={{
                                        backgroundColor: isTrafficView ? '#FFA500' : undefined,
                                        opacity: isCompareMode ? 0.5 : 1,
                                        cursor: isCompareMode ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    <MdSwapVert size={24} color="white" />
                                </button>
                                <button className='toggle-chart-button' onClick={toggleChartType}>
                                    {chartType === 'bar' ? (
                                    <BiLineChart size={24} title='Switch to Line Chart' color='white' />
                                    ) : (
                                    <BiBarChartAlt2 size={24} title='Switch to Bar Chart' color='white' />
                                    )}
                                </button>
                                <button
                                    className='toggle-chart-button'
                                    onClick={() => setIsFullscreen(prev => !prev)}
                                    title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                                >
                                    {isFullscreen ? (
                                    <BiExitFullscreen size={24} color="white" />
                                    ) : (
                                    <BiFullscreen size={24} color="white" />
                                    )}
                                </button>
                                </div>

                                {/* Percentage change */}
                                <div className='count-chart-footer-percentage-container'>
                                <div className='count-chart-footer-percentage-icon'>
                                    {monthlyVisitorPercentageIncrease > 0 ? (
                                    <svg style={{ color: '#32de84' }} xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-up-right-circle" viewBox="0 0 16 16">
                                        <path fillRule="evenodd" d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8zm15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.854 10.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707l-4.096 4.096z" />
                                    </svg>
                                    ) : (
                                    <svg style={{ color: '#EF5B5B' }} xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-down-right-circle" viewBox="0 0 16 16">
                                        <path fillRule="evenodd" d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8zm15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.854 5.146a.5.5 0 1 0-.708.708L9.243 9.95H6.475a.5.5 0 1 0 0 1h3.975a.5.5 0 0 0 .5-.5V6.475a.5.5 0 1 0-1 0v2.768L5.854 5.146z" />
                                    </svg>
                                    )}
                                </div>
                                <div
                                    className='count-chart-footer-percentage-value'
                                    style={{ color: monthlyVisitorPercentageIncrease > 0 ? '#32de84' : '#EF5B5B' }}
                                >
                                    {monthlyVisitorPercentageIncrease}%
                                </div>
                                </div>
                            </div>
                        </div>
                        <div className='chart-total-container'>
                            <div className='chart-total-pill'>
                                {isTrafficView ? (
                                    <div className="chart-total-label" style={{ color: '#FFA500' }}>
                                        Total Traffic (Average): {Math.round((totalPeopleIn + totalPeopleOut) / 2)}
                                    </div>
                                ) : (
                                    <>
                                        <div className="chart-total-label" style={{ color: '#64C6E6' }}>Total In: {totalPeopleIn}</div>
                                        <div className="chart-total-label" style={{ color: '#8E338C' }}>Total Out: {totalPeopleOut}</div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div>
                            {
                                chartType === 'bar' ?
                                    <Bar
                                        key={`monthly-bar-${chartKey}`}
                                        ref={barChartRef}
                                        data={
                                            isCompareMode && compareChartData
                                                ? compareChartData
                                                : (barChartData || { labels: [], datasets: [] })
                                        }
                                        options={getBarChartOptions()}
                                    />
                                    :
                                    <Line
                                        key={`monthly-line-${chartKey}`}
                                        ref={lineChartRef}
                                        data={
                                            isCompareMode && compareChartData
                                                ? compareChartData
                                                : (lineChartData || { labels: [], datasets: [] })
                                        }
                                        options={getLineChartOptions()}
                                    />


                            }

                            {/* <div className='chart-footer-container'>
                                <div className='chart-footer-label'>{monthlyVisitors} Total visitors for {getMonthName(selectedMonth)} {selectedYear}</div>
                            </div> */}
                            {
                                monthlyDataLoading && <div id="monthly-loading-overlay">
                                    <BeatLoader color={'white'} loading={monthlyDataLoading} size={15} />
                                </div>
                            }
                        </div>
                    </>
                    :
                    <div style={{ display: 'none' }} ></div>
            }
        </div>
    )
}

// Memoize component to prevent unnecessary re-renders
export default React.memo(MonthlyCountChart, (prevProps, nextProps) => {
    // Return true if props are equal (skip re-render), false if different (re-render)
    return (
        prevProps.selectedYear === nextProps.selectedYear &&
        prevProps.selectedMonth === nextProps.selectedMonth &&
        prevProps.monthlyDataLoading === nextProps.monthlyDataLoading &&
        prevProps.isTrafficView === nextProps.isTrafficView &&
        prevProps.isCompareMode === nextProps.isCompareMode &&
        // Compare data by reference (will change when new data fetched)
        prevProps.monthlyCountData === nextProps.monthlyCountData &&
        prevProps.rawMonthlyData === nextProps.rawMonthlyData &&
        prevProps.processedCameraData === nextProps.processedCameraData
    );
});